/* eslint-disable */
import { Auth0ContextInterface } from "@auth0/auth0-react";

export type LogoutReason =
	| "session_expired"
	| "user_action"
	| "access_denied"
	| "user_deactivated"
	| "session_timeout"
	| "token_refresh_failed";

const STORE_KEY = "UserStore";

/**
 * Centralized logout handler to ensure consistent cleanup across the application
 * @param clearUser - Function to clear user from store
 * @param logout - Auth0 logout function
 * @param reason - Reason for logout (for audit/logging purposes)
 */
export const centralizedLogout = async (
	clearUser: () => void,
	logout: Auth0ContextInterface["logout"],
	reason: LogoutReason,
) => {
	console.log(`[Auth] Logging out: ${reason}`);

	try {
		// Clear user state
		clearUser();

		// Clear all storage
		localStorage.removeItem(STORE_KEY);
		localStorage.removeItem("MenuStore");
		sessionStorage.clear();

		// Log audit event if user info is available
		// Note: This should be called before clearing user state in production
		// We're doing it after for safety in case audit fails
		try {
			await logAuditEvent(reason);
		} catch (error) {
			console.error("[Auth] Failed to log audit event:", error);
			// Don't block logout if audit fails
		}

		// Auth0 logout with federated logout
		await logout({
			logoutParams: {
				returnTo: window.location.origin,
				federated: true, // Force IDP logout
			},
		});
	} catch (error) {
		console.error("[Auth] Logout error:", error);
		// Force reload as fallback
		window.location.href = window.location.origin;
	}
};

/**
 * Log audit event for authentication actions
 * @param reason - Reason for the auth event
 */
const logAuditEvent = async (reason: LogoutReason): Promise<void> => {
	// Implementation depends on your audit service
	// This is a placeholder that should be implemented based on your audit requirements
	try {
		const auditData = {
			event: "LOGOUT",
			reason,
			timestamp: new Date().toISOString(),
			userAgent: navigator.userAgent,
			// Add more context as needed
		};

		console.log("[Auth] Audit event:", auditData);

		// TODO: Send to your audit API
		// await axios.post('/audit/auth', auditData);
	} catch (error) {
		console.error("[Auth] Failed to create audit log:", error);
	}
};

/**
 * Check if user data is stale based on last fetch timestamp
 * @param lastFetchedAt - Timestamp of last user data fetch
 * @param staleThresholdMs - Threshold in milliseconds (default: 5 minutes)
 * @returns true if data is stale or timestamp is missing
 */
export const isUserDataStale = (
	lastFetchedAt: number | null | undefined,
	staleThresholdMs: number = 5 * 60 * 1000,
): boolean => {
	if (!lastFetchedAt) return true;
	return Date.now() - lastFetchedAt > staleThresholdMs;
};

/**
 * Validate if an error is an Auth0 token error
 * @param error - Error object
 * @returns true if error is related to token refresh
 */
export const isTokenRefreshError = (error: any): boolean => {
	return (
		error?.error === "missing_refresh_token" ||
		error?.error === "invalid_grant" ||
		error?.error === "login_required"
	);
};

/**
 * Determine error type from API response
 * @param error - Error object
 * @returns Error type classification
 */
export const classifyError = (
	error: any,
):
	| "access_denied"
	| "network_error"
	| "server_error"
	| "unauthorized"
	| "unknown" => {
	if (!error) return "unknown";

	const status = error.response?.status;

	// 401 - Unauthorized (auth failure from API Gateway)
	if (status === 401) {
		return "unauthorized";
	}

	// 403/404 - Access denied or not found
	if (status === 403 || status === 404) {
		return "access_denied";
	}

	// Network errors (CORS, timeout, no response)
	if (!error.response || error.code === "ECONNABORTED") {
		return "network_error";
	}

	// 500+ - Server errors
	if (status && status >= 500) {
		return "server_error";
	}

	return "unknown";
};

/**
 * Constants for auth configuration
 */
export const AUTH_CONSTANTS = {
	STORE_KEY: "UserStore",
	MAX_LOGIN_ATTEMPTS: 3,
	USER_DATA_STALE_TIME: 5 * 60 * 1000, // 5 minutes
	TOKEN_REFRESH_INTERVAL: 5 * 60 * 1000, // 5 minutes
	SESSION_TIMEOUT: 30 * 60 * 1000, // 30 minutes
	MAX_REFRESH_SUBSCRIBERS: 100,
} as const;
